import { reactive } from 'vue'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import { AuthState } from '@/types/authState'
import {
  PagedMerchantResponse,
  MerchantQueryParams,
} from '@/types/commonMerchantGetMerchantData'
import { CommonMerchantShopifyStores } from '@/types/commonMerchantShopifyStores'
import type { ShopifyDevApp } from '@/types/shopifyDevApp'
import type { GatewayFilterRequest } from '@/types/gatewayFilter'
import type { MerchantExtensionsWebhook } from '@/types/dropIn'
import { normalizeError } from '@/util/errorNormalizer'

const MERCHANT = API_ENDPOINTS.COMMON_MERCHANT_SERVICE

/**
 * common-merchant returns the merchant's Engine secret as `Secret` on the
 * extensions webhook. The dashboard never reads, shows or sends it, so it is
 * dropped here and never reaches component state.
 */
function withoutEngineSecret(
  webhook: MerchantExtensionsWebhook & { Secret?: string },
): MerchantExtensionsWebhook {
  const cleaned = { ...webhook }
  delete cleaned.Secret
  return cleaned
}

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COMPLETE = 'COMPLETE',
}

interface CommonMerchantApi {
  state: API_STATES
  getMerchants(
    authState: AuthState,
    params?: MerchantQueryParams,
  ): Promise<PagedMerchantResponse>
  getShopifyStore(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    refreshShopifyStore?: boolean,
    includeDeleted?: boolean,
  ): Promise<CommonMerchantShopifyStores>
  deleteStore(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<CommonMerchantShopifyStores>
  updateBankAccountStatus(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    bankAccountId: string,
    isReach: boolean,
  ): Promise<void>
  updateIsReachTax(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    isReachTax: boolean,
  ): Promise<void>
  refreshBusinessEntities(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<CommonMerchantShopifyStores>
  createDevApp(
    authState: AuthState,
    merchantId: string,
    payload: {
      storeId: string
      clientId: string
      clientSecret: string
      branded: boolean
    },
  ): Promise<ShopifyDevApp>
  deleteDevApp(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<void>
  restoreDevApp(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    credentials: { clientId: string; clientSecret: string },
  ): Promise<void>
  offboardStore(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    payload: { OffboardDate: string; OffboardCoolingOffDays: number | null },
  ): Promise<void>
  setGatewayFilter(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    payload: GatewayFilterRequest,
  ): Promise<void>
  clearGatewayFilter(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<void>
  // The extensions-webhook methods leave `state` alone: the Drop-In tab owns
  // its own lifecycle and must not clobber the merchant list's.
  getExtensionsWebhook(
    authState: AuthState,
    merchantId: string,
  ): Promise<MerchantExtensionsWebhook | null>
  saveExtensionsWebhook(
    authState: AuthState,
    merchantId: string,
    webhookUrl: string,
  ): Promise<MerchantExtensionsWebhook>
  deleteExtensionsWebhook(authState: AuthState, merchantId: string): Promise<void>
}

// available as global state
export const commonMerchantApi: CommonMerchantApi = reactive({
  state: API_STATES.INITIAL,

  getMerchants(
    authState: AuthState,
    params?: MerchantQueryParams,
  ): Promise<PagedMerchantResponse> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .get<PagedMerchantResponse>(MERCHANT.ALL_MERCHANTS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          Id: params?.Id,
          Name: params?.Name,
          State: params?.State,
          page: params?.page ?? 0,
          size: params?.size ?? 20,
          sort: params?.sort,
        },
      })
      .then(({ data }) => {
        commonMerchantApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  getShopifyStore(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    refreshShopifyStore: boolean = false,
    includeDeleted: boolean = false,
  ): Promise<CommonMerchantShopifyStores> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .get<CommonMerchantShopifyStores>(
        `${MERCHANT.ALL_MERCHANTS}/${merchantId}/shopify/store/${storeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            RefreshShopifyStore: refreshShopifyStore,
            IncludeDeleted: includeDeleted,
          },
        },
      )
      .then(({ data }) => {
        if (data) {
          commonMerchantApi.state = API_STATES.COMPLETE
          return data
        }
        throw new Error('Unexpected response')
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },

  deleteStore(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<CommonMerchantShopifyStores> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .delete(
        `${MERCHANT.ALL_MERCHANTS}/${merchantId}/shopify/store/${storeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(response => {
        commonMerchantApi.state = API_STATES.COMPLETE
        return response.data || {}
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  updateBankAccountStatus(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    bankAccountId: string,
    isReach: boolean,
  ): Promise<void> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .patch(
        `${MERCHANT.ALL_MERCHANTS}/${merchantId}/shopify/store/${storeId}/business-entity/${businessEntityId}/bank-account/${bankAccountId}`,
        { IsReach: isReach },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        commonMerchantApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  updateIsReachTax(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    businessEntityId: string,
    isReachTax: boolean,
  ): Promise<void> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .patch(
        `${MERCHANT.ALL_MERCHANTS}/${merchantId}/shopify/store/${storeId}/business-entity/${businessEntityId}`,
        { IsReachTax: isReachTax },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        commonMerchantApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  refreshBusinessEntities(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<CommonMerchantShopifyStores> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .get(`${MERCHANT.ALL_MERCHANTS}/${merchantId}/shopify/store/${storeId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          RefreshShopifyStore: true,
        },
      })
      .then(({ data }) => {
        if (data) {
          commonMerchantApi.state = API_STATES.COMPLETE
          return data
        }
        throw new Error('Unexpected response')
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  createDevApp(
    authState: AuthState,
    merchantId: string,
    payload: {
      storeId: string
      clientId: string
      clientSecret: string
      branded: boolean
    },
  ): Promise<ShopifyDevApp> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    // Transform to PascalCase for API
    const apiPayload = {
      StoreId: payload.storeId,
      ClientId: payload.clientId,
      ClientSecret: payload.clientSecret,
      Branded: payload.branded,
    }

    return axios
      .post<ShopifyDevApp>(MERCHANT.SHOPIFY_DEV_APP(merchantId), apiPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        commonMerchantApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  deleteDevApp(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<void> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .delete(MERCHANT.SHOPIFY_DEV_APP_BY_STORE(merchantId, storeId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        commonMerchantApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  restoreDevApp(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    credentials: { clientId: string; clientSecret: string },
  ): Promise<void> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    const apiPayload = {
      ClientId: credentials.clientId,
      ClientSecret: credentials.clientSecret,
    }

    return axios
      .post(MERCHANT.RESTORE_DEV_APP(merchantId, storeId), apiPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        commonMerchantApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        if (err.response?.data) {
          throw err.response.data
        }
        throw err
      })
  },
  offboardStore(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    payload: { OffboardDate: string; OffboardCoolingOffDays: number | null },
  ): Promise<void> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .patch(MERCHANT.SHOPIFY_STORE_OFFBOARD(merchantId, storeId), payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        commonMerchantApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  setGatewayFilter(
    authState: AuthState,
    merchantId: string,
    storeId: string,
    payload: GatewayFilterRequest,
  ): Promise<void> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .put(MERCHANT.SHOPIFY_STORE_GATEWAY_FILTER(merchantId, storeId), payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        commonMerchantApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  clearGatewayFilter(
    authState: AuthState,
    merchantId: string,
    storeId: string,
  ): Promise<void> {
    const token = authState.user?.accessToken
    commonMerchantApi.state = API_STATES.LOADING

    return axios
      .delete(MERCHANT.SHOPIFY_STORE_GATEWAY_FILTER(merchantId, storeId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        commonMerchantApi.state = API_STATES.COMPLETE
      })
      .catch(err => {
        commonMerchantApi.state = API_STATES.ERROR
        throw normalizeError(err)
      })
  },
  /** Resolves `null` when the merchant has no extensions webhook (404). */
  getExtensionsWebhook(
    authState: AuthState,
    merchantId: string,
  ): Promise<MerchantExtensionsWebhook | null> {
    return axios
      .get<MerchantExtensionsWebhook>(MERCHANT.EXTENSIONS_WEBHOOK(merchantId), {
        headers: { Authorization: `Bearer ${authState.user?.accessToken}` },
      })
      .then(({ data }) => withoutEngineSecret(data))
      .catch(err => {
        const apiError = normalizeError(err)
        if (apiError.isNotFoundError()) return null
        throw apiError
      })
  },
  saveExtensionsWebhook(
    authState: AuthState,
    merchantId: string,
    webhookUrl: string,
  ): Promise<MerchantExtensionsWebhook> {
    return axios
      .put<MerchantExtensionsWebhook>(
        MERCHANT.EXTENSIONS_WEBHOOK(merchantId),
        { WebhookUrl: webhookUrl },
        { headers: { Authorization: `Bearer ${authState.user?.accessToken}` } },
      )
      .then(({ data }) => withoutEngineSecret(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  deleteExtensionsWebhook(authState: AuthState, merchantId: string): Promise<void> {
    return axios
      .delete(MERCHANT.EXTENSIONS_WEBHOOK(merchantId), {
        headers: { Authorization: `Bearer ${authState.user?.accessToken}` },
      })
      .then(() => undefined)
      .catch(err => {
        throw normalizeError(err)
      })
  },
})
